import { Config, FeatureFlags } from '../domain/config';
import { DocumentUploadState } from '../domain/document-collection';
import { FleetBase } from '../domain/fleet';
import { OtherIndividualInfo, PersonalInfo } from '../domain/person';
import { AnalyticsData, CreditTerms, Person } from '../domain/qualification';
import { MxEvent } from '../features/bank-account-connection/hooks/post-message-listener/PostMessageListener';

function config(overrides?: Partial<Config>): Config {
  return {
    documents: [
      {
        key: 'card_program_terms',
        name: 'Card Program Terms',
        url: 'https://test-card-program-terms/',
      },
    ],
    persona: {
      templateId: 'test-id',
      environment: 'sandbox',
    },
    segment: {
      writeKey: 'UNIT-TEST',
      cdnUrl: 'http://localhost/segment',
    },
    statsig: {
      clientKey: 'UNIT-TEST-STATSIG',
    },
    verifyRevenue: {
      pollSeconds: 5,
      maxWaitSeconds: 100,
    },
    verifyAppUrl: 'http://verify.localhost',
    creditAppUrl: 'http://localhost',
    ...overrides,
  };
}

function otherIndividualInfo(overrides?: Partial<OtherIndividualInfo>): OtherIndividualInfo {
  return {
    ...personalInfo(),
    uiId: 'abc',
    phone: '+1-212-555-6789',
    ssnLast4: '9876',
    ...overrides,
  };
}

function personalInfo(overrides?: Partial<PersonalInfo>): PersonalInfo {
  return {
    firstName: 'Linda',
    lastName: 'Hamm',
    dob: '10/14/1985',
    address: {
      streetAddress: '1313 Rockson Blvd',
      city: 'Richmond',
      state: 'WA',
      zip: '22314',
    },
    authorizedRep: false,
    beneficialOwner: false,
    controlPerson: false,
    ssnLast4: '',
    ...overrides,
  };
}

function makeDefaultFleetBase(): FleetBase {
  return {
    name: '',
    dbaName: null,
    address: {
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
    },
    shippingAddress: null,
    numCardsToOrder: null,
    ein: '',
    yearsOfOperation: '',
    businessType: '',
    industryId: '',
    annualRevenue: null,
    fleetSize: null,
    monthlyFuelSpend: null,
    monthlyNonFuelSpend: null,
    defAndVehicleSupplies: null,
    iceWaterFood: null,
    fleetAndMaintenance: null,
    travelAndRestaurants: null,
    materialsAndSupplies: null,
    generalBusiness: null,
    cardRestriction: null,
  };
}

function businessInfo(overrides?: Partial<FleetBase>): FleetBase {
  return {
    ...makeDefaultFleetBase(),
    name: 'Bronco Dozers',
    businessType: 'LLC',
    industryId: 'auto_repair',
    annualRevenue: 5_000_000_00,
    monthlyFuelSpend: 5_400_00,
    fleetSize: 10,
    address: {
      streetAddress: '89 Commercial Avenue',
      streetAddress2: 'Suite 789',
      city: 'Walla Walla',
      state: 'WA',
      zip: '55624',
    },
    yearsOfOperation: 'Between 2 and 5 years',
    monthlyNonFuelSpend: 0,
    defAndVehicleSupplies: false,
    iceWaterFood: false,
    fleetAndMaintenance: false,
    travelAndRestaurants: false,
    materialsAndSupplies: false,
    generalBusiness: false,
    ...overrides,
  };
}

function analyticsData(overrides?: Partial<AnalyticsData>): AnalyticsData {
  return {
    userId: 'user-id-25890',
    customerId: 'customer-id-8970',
    fleetUserExists: false,
    ...overrides,
  };
}

function creditTerms(overrides?: Partial<CreditTerms>): CreditTerms {
  return {
    finalCreditLimit: 17_000_00,
    billingCycle: 30,
    gracePeriodDays: 10,
    ...overrides,
  };
}

function requiredDocument(overrides?: Partial<DocumentUploadState>): DocumentUploadState {
  return {
    entityToken: 'test-entity-token',
    entityName: 'test-entity-name',
    documentName: 'test-doc-name',
    file: null,
    uploading: false,
    progress: 0,
    progressComputable: true,
    ...overrides,
  };
}

function person(overrides?: Partial<Person>): Person {
  return {
    id: 1,
    lastName: 'Smith',
    firstName: 'John',
    phone: '+1-212-765-4321',
    dob: '2002-20-02',
    address: {
      streetAddress: '100 Elm Street',
      city: 'Springwood',
      state: 'OH',
      zip: '98765',
    },
    beneficialOwner: false,
    controlPerson: false,
    authorizedRep: false,
    isApplicant: false,
    ...overrides,
  };
}

const mxMessage = ({ type, metadata }: Pick<MxEvent, 'type' | 'metadata'>) =>
  new MessageEvent('message', {
    origin: 'http://localhost',
    data: {
      mx: true,
      type,
      metadata,
    },
  });

const nonMxMessage = () =>
  new MessageEvent('message', {
    origin: 'http://localhost',
    data: {},
  });

const featureFlags = (overrides?: Partial<FeatureFlags>): FeatureFlags => {
  return {
    bfcTestimonialPanelExperiment: 'control',
    enableLinkBankAccount: true,
    enableSupportNumber: true,
    qfEnableAddressAutocomplete: false,
    fgEnablePreQualificationQuiz: false,
    fgEnableProgressIndicator: false,
    ...overrides,
  };
};

export {
  analyticsData,
  config,
  personalInfo,
  businessInfo,
  creditTerms,
  requiredDocument,
  otherIndividualInfo,
  person,
  mxMessage,
  nonMxMessage,
  featureFlags,
  makeDefaultFleetBase,
};
